/**
 * Mobile Context Utilities for Sentry
 *
 * Provides mobile-specific context, device detection, and breadcrumb collection
 * for enhanced mobile debugging of notification issues.
 */

import * as Sentry from '@sentry/sveltekit';

export interface MobileContext {
  isMobile: boolean;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  operatingSystem: string;
  browser: string;
  browserVersion: string;
  connectivity: {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
  };
  battery?: {
    charging: boolean;
    level: number;
  };
  memory?: {
    deviceMemory?: number;
    totalJSHeapSize?: number;
    usedJSHeapSize?: number;
  };
  [key: string]: unknown;
}

export interface NotificationContext {
  permissionStatus: NotificationPermission | 'unsupported';
  fcmSupported: boolean;
  serviceWorkerSupported: boolean;
  pushManagerSupported: boolean;
  notificationApiSupported: boolean;
  [key: string]: unknown;
}

/**
 * Device detection utilities
 */
export class DeviceDetector {
  static getDeviceInfo(): Partial<MobileContext> {
    const userAgent = navigator.userAgent;

    // Detect device type
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isTablet = /iPad|Android(?!.*Mobile)/i.test(userAgent);
    const deviceType = isTablet ? 'tablet' : (isMobile ? 'mobile' : 'desktop');

    // Detect operating system
    let operatingSystem = 'Unknown';
    if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
      operatingSystem = 'iOS';
    } else if (userAgent.includes('Android')) {
      operatingSystem = 'Android';
    } else if (userAgent.includes('Windows')) {
      operatingSystem = 'Windows';
    } else if (userAgent.includes('Mac')) {
      operatingSystem = 'macOS';
    } else if (userAgent.includes('Linux')) {
      operatingSystem = 'Linux';
    }

    // Detect browser
    let browser = 'Unknown';
    let browserVersion = '';

    if (userAgent.includes('Chrome')) {
      browser = 'Chrome';
      const match = userAgent.match(/Chrome\/(\d+\.\d+)/);
      browserVersion = match ? match[1] : '';
    } else if (userAgent.includes('Firefox')) {
      browser = 'Firefox';
      const match = userAgent.match(/Firefox\/(\d+\.\d+)/);
      browserVersion = match ? match[1] : '';
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      browser = 'Safari';
      const match = userAgent.match(/Version\/(\d+\.\d+)/);
      browserVersion = match ? match[1] : '';
    } else if (userAgent.includes('Edge')) {
      browser = 'Edge';
      const match = userAgent.match(/Edge\/(\d+\.\d+)/);
      browserVersion = match ? match[1] : '';
    }

    return {
      isMobile,
      deviceType,
      operatingSystem,
      browser,
      browserVersion
    };
  }

  static async getConnectivityInfo(): Promise<MobileContext['connectivity']> {
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      return {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
        saveData: connection.saveData
      };
    }
    return {};
  }

  static async getBatteryInfo(): Promise<MobileContext['battery']> {
    if ('getBattery' in navigator) {
      try {
        const battery = await (navigator as any).getBattery();
        return {
          charging: battery.charging,
          level: battery.level
        };
      } catch (error) {
        console.warn('Battery API not available:', error);
      }
    }
    return undefined;
  }

  static getMemoryInfo(): MobileContext['memory'] {
    if ('deviceMemory' in navigator) {
      return {
        deviceMemory: (navigator as any).deviceMemory
      };
    }

    // Get JS heap size if performance.memory is available
    if ('performance' in window && 'memory' in (window.performance as any)) {
      const memory = (window.performance as any).memory;
      return {
        totalJSHeapSize: memory.totalJSHeapSize,
        usedJSHeapSize: memory.usedJSHeapSize
      };
    }

    return {};
  }
}

/**
 * Notification capability detection
 */
export class NotificationDetector {
  static getNotificationContext(): NotificationContext {
    return {
      permissionStatus: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
      fcmSupported: typeof window !== 'undefined' && 'messaging' in window,
      serviceWorkerSupported: 'serviceWorker' in navigator,
      pushManagerSupported: 'serviceWorker' in navigator && 'PushManager' in window,
      notificationApiSupported: 'Notification' in window
    };
  }
}

/**
 * Mobile context collector for Sentry
 */
export class MobileContextCollector {
  private static context: MobileContext | null = null;

  /**
   * Collect comprehensive mobile context
   */
  static async collectContext(): Promise<MobileContext> {
    if (this.context) {
      return this.context;
    }

    const deviceInfo = DeviceDetector.getDeviceInfo();
    const connectivity = await DeviceDetector.getConnectivityInfo();
    const battery = await DeviceDetector.getBatteryInfo();
    const memory = DeviceDetector.getMemoryInfo();

    this.context = {
      ...deviceInfo,
      connectivity,
      battery,
      memory,
      isMobile: deviceInfo.isMobile || false,
      deviceType: deviceInfo.deviceType || 'desktop',
      operatingSystem: deviceInfo.operatingSystem || 'Unknown',
      browser: deviceInfo.browser || 'Unknown',
      browserVersion: deviceInfo.browserVersion || 'Unknown'
    } as MobileContext;

    return this.context;
  }

  /**
   * Set mobile context for Sentry
   */
  static async setSentryContext(): Promise<void> {
    try {
      const context = await this.collectContext();
      const notificationContext = NotificationDetector.getNotificationContext();

      // Set device context
      Sentry.setContext('mobile_device', {
        deviceType: context.deviceType,
        operatingSystem: context.operatingSystem,
        browser: `${context.browser} ${context.browserVersion}`,
        isMobile: context.isMobile,
        connectivity: context.connectivity,
        battery: context.battery,
        memory: context.memory
      });

      // Set notification capabilities context
      Sentry.setContext('notification_capabilities', notificationContext);

      // Set mobile-specific tags
      Sentry.setTag('device.type', context.deviceType);
      Sentry.setTag('device.os', context.operatingSystem);
      Sentry.setTag('device.browser', context.browser);
      Sentry.setTag('device.mobile', context.isMobile.toString());
      Sentry.setTag('notifications.permission', notificationContext.permissionStatus);
      Sentry.setTag('notifications.fcm_supported', notificationContext.fcmSupported.toString());
      Sentry.setTag('notifications.sw_supported', notificationContext.serviceWorkerSupported.toString());

      console.log('[MobileContext] Sentry context set successfully');
    } catch (error) {
      console.warn('[MobileContext] Failed to set Sentry context:', error);
    }
  }

  /**
   * Add mobile-specific breadcrumb
   */
  static addBreadcrumb(message: string, category: string = 'mobile', data?: any): void {
    Sentry.addBreadcrumb({
      message,
      category,
      level: 'info',
      data: {
        timestamp: new Date().toISOString(),
        mobile: this.context?.isMobile || false,
        deviceType: this.context?.deviceType || 'unknown',
        ...data
      }
    });
  }

  /**
   * Track notification permission changes
   */
  static trackPermissionChange(oldPermission: NotificationPermission, newPermission: NotificationPermission): void {
    this.addBreadcrumb('Notification permission changed', 'notifications', {
      oldPermission,
      newPermission,
      changeTime: new Date().toISOString()
    });

    Sentry.setTag('notifications.permission', newPermission);
  }

  /**
   * Track Firebase/FCM events
   */
  static trackFCMEvent(event: string, data?: any): void {
    this.addBreadcrumb(`FCM: ${event}`, 'fcm', data);
  }

  /**
   * Track service worker events
   */
  static trackServiceWorkerEvent(event: string, data?: any): void {
    this.addBreadcrumb(`SW: ${event}`, 'service-worker', data);
  }

  /**
   * Track connectivity changes
   */
  static trackConnectivityChange(connectivity: MobileContext['connectivity']): void {
    this.addBreadcrumb('Connectivity changed', 'network', connectivity);
    Sentry.setTag('network.effectiveType', connectivity.effectiveType || 'unknown');
  }
}

/**
 * Initialize mobile context collection
 */
export async function initializeMobileContext(): Promise<void> {
  try {
    await MobileContextCollector.setSentryContext();

    // Monitor connectivity changes
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      const handleChange = () => {
        MobileContextCollector.trackConnectivityChange({
          effectiveType: connection.effectiveType,
          downlink: connection.downlink,
          rtt: connection.rtt,
          saveData: connection.saveData
        });
      };

      connection.addEventListener('change', handleChange);
    }

    // Set up service worker error monitoring
    setupServiceWorkerErrorMonitoring();

    console.log('[MobileContext] Mobile context monitoring initialized');
  } catch (error) {
    console.warn('[MobileContext] Failed to initialize mobile context:', error);
  }
}

/**
 * Set up monitoring for service worker errors
 */
function setupServiceWorkerErrorMonitoring() {
  // Listen for service worker errors
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'SW_ERROR') {
        const { error } = event.data;

        // Report service worker error to Sentry
        Sentry.captureException(new Error(`[SW] ${error.context}: ${error.message}`), {
          tags: {
            'error.source': 'service_worker',
            'sw.context': error.context
          },
          extra: {
            swTimestamp: error.timestamp,
            swUserAgent: error.userAgent,
            swUrl: error.url,
            swStack: error.stack
          }
        });

        // Add breadcrumb for SW error
        MobileContextCollector.addBreadcrumb('Service worker error received', 'service-worker', {
          context: error.context,
          message: error.message
        });
      }
    });
  }

  // Monitor for SW errors in console
  const originalConsoleError = console.error;
  console.error = (...args) => {
    originalConsoleError.apply(console, args);

    // Look for service worker related errors
    const errorString = args.join(' ');
    if (typeof errorString === 'string' && (
      errorString.includes('Service Worker') ||
      errorString.includes('[SW]') ||
      errorString.includes('Firebase') ||
      errorString.includes('messaging')
    )) {
      MobileContextCollector.addBreadcrumb('Service worker console error detected', 'service-worker', {
        error: errorString.substring(0, 200)
      });
    }
  };
}